// src/pages/checkout.jsx - FIXED VERSION WITH IMMEDIATE CART CLEAR

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useCart } from '../context/cartContext';
import api from '../store/api/base';
import Loader from '../components/common/loader';
import { setError, setSuccess } from '../store/slices/uiSlice';
import { fetchCart } from '../store/slices/cartSlice';  // ✅ Import fetchCart

// Components
import AddressList from '../components/checkout/addressList';
import AddressForm from '../components/checkout/addressForm';
import OrderSummary from '../components/checkout/orderSummary';
import PaymentMethod from '../components/checkout/paymentMethod';

const Checkout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector(state => state.auth);
  const { cart, itemCount, refreshCart } = useCart();
  
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [billingAddress, setBillingAddress] = useState('same');
  const [paymentMethod, setPaymentMethod] = useState('PHONEPE'); // ✅ Default to PHONEPE
  const [orderNotes, setOrderNotes] = useState('');
  const [summaryPricing, setSummaryPricing] = useState(null); // real pricing from Shiprocket
  const [summaryLoading, setSummaryLoading] = useState(false);
  
  // Redirect if not authenticated or cart is empty
  // Auth check — runs once on mount
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?redirect=checkout');
      return;
    }
    fetchAddresses();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Empty cart redirect — separate effect so it doesn't re-trigger fetchAddresses
  useEffect(() => {
    if (!isAuthenticated) return;
    const checkCartAfterSync = setTimeout(() => {
      if (itemCount === 0) {
        // console.warn('⚠️ Cart is empty at checkout, redirecting to cart page');
        dispatch(setError('Your cart is empty'));
        navigate('/cart');
      }
    }, 1000);
    return () => clearTimeout(checkCartAfterSync);
  }, [itemCount]); // eslint-disable-line react-hooks/exhaustive-deps
  
  const fetchAddresses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/checkout/addresses');
      // console.log('📍 Addresses fetched:', response.data);
      
      setAddresses(response.data.data.addresses);
      
      const defaultAddress = response.data.data.addresses.find(addr => addr.isDefault)
        || response.data.data.addresses[0]; // fallback to first address if none is default
      if (defaultAddress) {
        setSelectedAddress(defaultAddress.addressId);
        fetchSummaryForAddress(defaultAddress.pincode);
      }
      
      setLoading(false);
    } catch (error) {
      // console.error('❌ Error fetching addresses:', error);
      dispatch(setError('Failed to load addresses'));
      setLoading(false);
    }
  };
  
  // Fetch real shipping cost from Shiprocket whenever address changes
  // pincode passed directly — no state lookup needed
  const fetchSummaryForAddress = async (pincode) => {
    if (!pincode) {
      dispatch(setError('Selected address has no pincode. Please update your address.'));
      return;
    }
    try {
      setSummaryLoading(true);
      // Don't clear summaryPricing here — keep showing previous value while loading
      const response = await api.get('/checkout/summary', {
        params: { pincode }
      });
      if (response.data?.data?.summary) {
        const pricing = response.data.data.summary.pricing;
setSummaryPricing(pricing);
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Unable to calculate shipping for this address.';
      dispatch(setError(msg));
      setSummaryPricing(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleAddressSelect = (addressId, address) => {
    // console.log('📍 Selected address:', addressId, 'pincode:', address?.pincode);
    setSelectedAddress(addressId);
    if (address?.pincode) fetchSummaryForAddress(address.pincode);
  };
  
  const handleAddressSubmit = async (addressData) => {
    try {
      setLoading(true);
      const response = await api.post('/checkout/addresses', addressData);
      
      setAddresses([...addresses, response.data.data.address]);
      setSelectedAddress(response.data.data.address.addressId);
      setShowAddressForm(false);
      dispatch(setSuccess('Address added successfully'));
      
      setLoading(false);
    } catch (error) {
      // console.error('❌ Error adding address:', error);
      dispatch(setError(error.response?.data?.message || 'Failed to add address'));
      setLoading(false);
    }
  };
  
  const handlePaymentMethodChange = (method) => {
    // console.log('💳 Payment method changed:', method);
    setPaymentMethod(method);
  };
  
const handlePlaceOrder = async () => {
   
    if (!selectedAddress) {
      dispatch(setError('Please select a shipping address'));
      return;
    }
    
    if (itemCount === 0) {
      dispatch(setError('Your cart is empty'));
      navigate('/cart');
      return;
    }
    
    try {
      setLoading(true);
      
      // Build order data - payment method always PHONEPE
      const orderData = {
        shippingAddressId: selectedAddress,
        billingAddressId: billingAddress === 'same' ? 'same' : billingAddress,
        paymentMethod: 'PHONEPE', // ✅ Always PHONEPE

        customerType: 'B2C',
        notes: orderNotes || undefined
      };
      
      // console.log('📦 Sending order data:', orderData);
      
      const response = await api.post('/checkout/place-order', orderData);
      
      // console.log('✅ Order response:', response.data);
      
      setLoading(false);
      
      const isExistingOrder = response.data.data.isExisting;
      
      if (isExistingOrder) {
        // console.log('⚠️ Existing order found, redirecting to payment...');
        dispatch(setSuccess('Redirecting to your pending order...'));
      }
      
      // ✅ Always redirect to payment page (no COD)
      // console.log('💳 Redirecting to payment page...');
      
      setTimeout(() => {
        navigate(`/payment/${response.data.data.orderId}`, { replace: true });
      }, 100);
      
    } catch (error) {
      // console.error('❌ Error placing order:', error);
      // console.error('Error response:', error.response?.data);
      
      const errorMessage = error.response?.data?.message || 'Failed to place order';
      dispatch(setError(errorMessage));
      setLoading(false);
    }
  };

  
  if (loading && addresses.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center">
        <Loader size="lg" />
      </div>
    );
  }
  
   return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Checkout</h1>
      
      {itemCount === 0 && (
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-3 rounded mb-6">
          <p className="font-medium">Your cart is empty. Redirecting...</p>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main checkout column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Address section */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Shipping Address</h2>
              <button 
                className="text-blue-600 hover:underline text-sm font-medium"
                onClick={() => setShowAddressForm(!showAddressForm)}
              >
                {showAddressForm ? 'Cancel' : '+ Add New Address'}
              </button>
            </div>
            
            {showAddressForm ? (
              <AddressForm onSubmit={handleAddressSubmit} />
            ) : (
              <AddressList 
                addresses={addresses} 
                selectedAddress={selectedAddress}
                onSelect={handleAddressSelect}
              />
            )}
          </div>
          
          {/* Billing address option */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">Billing Address</h2>
            
            <div className="space-y-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="billingAddress"
                  checked={billingAddress === 'same'}
                  onChange={() => setBillingAddress('same')}
                  className="mr-2 w-4 h-4"
                />
                <span className="text-gray-700">Same as shipping address</span>
              </label>
              
              {addresses.map(address => (
                <label key={address.addressId} className="flex items-center cursor-pointer">
                  <input
                    type="radio"
                    name="billingAddress"
                    checked={billingAddress === address.addressId}
                    onChange={() => setBillingAddress(address.addressId)}
                    className="mr-2 w-4 h-4"
                  />
                  <span className="text-gray-700">
                    {address.fullName}, {address.addressLine1}, {address.city}
                  </span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Payment method */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">Payment Method</h2>
            
            <PaymentMethod 
              selected={paymentMethod}
              onChange={handlePaymentMethodChange}
            />
          </div>
          
          {/* Order notes */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">Order Notes (Optional)</h2>
            
            <textarea
              value={orderNotes}
              onChange={(e) => setOrderNotes(e.target.value)}
              placeholder="Any special instructions for your order..."
              className="w-full border border-gray-300 rounded-lg p-3 h-24 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              maxLength={500}
            />
            <p className="text-xs text-gray-500 mt-2">
              Maximum 500 characters
            </p>
          </div>
        </div>
        
        {/* Order summary column */}
        <div className="lg:col-span-1">
          <div className="sticky top-20 space-y-4">
            {summaryLoading && (
              <div className="text-xs text-blue-500 text-center py-1 animate-pulse">
                Calculating shipping...
              </div>
            )}
            <OrderSummary cart={cart} summaryPricing={summaryPricing} />
            
            {/* Place Order Button */}
            <div className="bg-white rounded-lg shadow-md p-4">
              <button
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 active:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600 shadow-sm"
                onClick={handlePlaceOrder}
                disabled={loading || !selectedAddress || itemCount === 0}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </span>
                ) : (
                  'Proceed to Payment'
                )}
              </button>
              
              <div className="mt-3 text-center">
                <p className="text-xs text-gray-500">
                  By placing your order, you agree to our{' '}
                  <a href="/terms-of-service" className="text-blue-600 hover:underline">Terms & Conditions</a>
                </p>
              </div>
              
              <div className="mt-3 text-center">
                <button
                  onClick={() => navigate('/products')}
                  className="text-blue-600 hover:underline text-sm font-medium"
                >
                  ← Continue Shopping
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;